/**
 * Layout engine.
 *
 * Given a caption segment's words, a resolved style, a placement and the output
 * frame size, compute where every word sits. Font sizes/strokes are expressed
 * at REFERENCE_HEIGHT and scaled to the real frame so a template looks the same
 * at 720p and 1080p.
 *
 * Layout is computed at *resting* size. Active-word / emphasis scaling is
 * applied as a transform around each word's center at draw time, so a growing
 * word does not reflow its neighbours (which would cause jitter).
 */
import { REFERENCE_HEIGHT } from './style.js';
import { resolveWordLook, rowStyleAt } from './word-look.js';
function applyTransform(text, transform) {
    switch (transform) {
        case 'uppercase':
            return text.toUpperCase();
        case 'lowercase':
            return text.toLowerCase();
        case 'capitalize':
            return text.replace(/(^|\s)(\p{L})/gu, (_m, s, c) => s + c.toUpperCase());
        default:
            return text;
    }
}
export function displayText(word, style) {
    return applyTransform(word.text, style.textTransform);
}
/**
 * Families appended after the primary so glyphs the Latin caption fonts LACK -
 * notably the ₹ sign (U+20B9), which the bundled Inter/Poppins/Montserrat/
 * Fraunces subsets do NOT include - render in a consistent, registered font
 * (NotoSansDevanagari does have ₹) via per-glyph fallback, instead of a random
 * per-OS system fallback that looks different. Used by both preview and export.
 */
export const FONT_FALLBACK_STACK = '"NotoSansDevanagari", sans-serif';
export function cssFont(family, weight, sizePx) {
    return `${weight} ${sizePx}px "${family}", ${FONT_FALLBACK_STACK}`;
}
/** Resolve vertical center (frame px) from anchor + explicit yNorm. */
function anchorY(placement, frame, boxHeight) {
    if (placement.yNorm > 0)
        return placement.yNorm * frame.height;
    const pad = frame.height * 0.06;
    switch (placement.anchor) {
        case 'top':
            return pad + boxHeight / 2;
        case 'upper':
            return frame.height * 0.28;
        case 'center':
            return frame.height * 0.5;
        case 'lower':
            return frame.height * 0.72;
        case 'bottom':
        default:
            return frame.height - pad - boxHeight / 2;
    }
}
export function layoutSegment(words, style, placement, frame, measurer) {
    const scale = frame.height / REFERENCE_HEIGHT;
    const fontSizePx = style.fontSizePx * scale;
    const lineHeightPx = fontSizePx * style.lineHeight;
    const letterSpacing = style.letterSpacingPx * scale;
    const font = cssFont(style.fontFamily, style.fontWeight, fontSizePx);
    const boldFont = cssFont(style.fontFamily, Math.max(style.fontWeight, 700), fontSizePx);
    const maxWidth = placement.maxWidthNorm * frame.width;
    // Floor the inter-word gap so subset fonts with a narrow space glyph - and
    // active/emphasized words that scale up around their center - keep breathing
    // room and never visually collide with neighbours.
    const spaceWidth = Math.max(measurer.measure(' ', font), fontSizePx * 0.32);
    // 1. Measure every word with its resolved look (per-word style > emphasis >
    //    caption). Devanagari words always use a Devanagari font, so Hinglish
    //    lines render correctly instead of showing tofu boxes.
    const measureWord = (ws, row, sizeMul) => {
        const text = displayText(ws.word, style);
        const look = resolveWordLook(style, ws.word, row == null ? null : rowStyleAt(style, row));
        const px = fontSizePx * sizeMul;
        const wFont = cssFont(look.family, look.weight, px);
        // Words reserve room for any visual up-scale (emphasis + per-word size) AND
        // any pill's side padding, so a bigger word never overflows its pill or
        // collides with neighbours.
        const emScale = (ws.word.emphasized ? style.emphasis.scale || 1 : 1) * (ws.word.sizeScale ?? 1);
        const pill = (ws.word.emphasized && style.emphasis.highlightColor && !style.emphasis.newLine) || look.highlightColor;
        const pillPad = pill ? px * 0.3 : 0;
        const glyphW = measurer.measure(text, wFont) + letterSpacing * sizeMul * Math.max(0, [...text].length - 1);
        const w = glyphW * emScale + pillPad * 2;
        return { ws, text, width: w, cssFont: wFont, look, fontPx: px };
    };
    const measured = words.map((ws) => measureWord(ws, null, 1));
    // 2. Greedy line break within maxWidth. When `emphasis.newLine` is set, an
    //    emphasized/key word is pulled onto its OWN line (break before AND after)
    //    so it sits alone, bigger, below the rest of the caption. A word whose
    //    style asks for `breakAfter` ends its row.
    const newLineEmphasis = Boolean(style.emphasis.newLine);
    const lines = [[]];
    let lineWidth = 0;
    for (const m of measured) {
        const isolate = newLineEmphasis && m.ws.word.emphasized;
        // Start a fresh line before an isolated key word (if the line has content).
        if (isolate && lines[lines.length - 1].length > 0) {
            lines.push([]);
            lineWidth = 0;
        }
        const addWidth = (lines[lines.length - 1].length > 0 ? spaceWidth : 0) + m.width;
        if (!isolate && lineWidth + addWidth > maxWidth && lines[lines.length - 1].length > 0) {
            lines.push([]);
            lineWidth = 0;
        }
        lines[lines.length - 1].push(m);
        lineWidth += (lines[lines.length - 1].length > 1 ? spaceWidth : 0) + m.width;
        // Force following words onto a new line so the key word stays alone.
        if (isolate || m.ws.word.wordStyle?.breakAfter) {
            lines.push([]);
            lineWidth = 0;
        }
    }
    // Drop any trailing empty line left by an isolated key word at the very end.
    while (lines.length > 1 && lines[lines.length - 1].length === 0)
        lines.pop();
    // Drop empty rows in the middle too (two forced breaks in a row).
    for (let i = lines.length - 1; i >= 0; i--)
        if (lines.length > 1 && lines[i].length === 0)
            lines.splice(i, 1);
    // 2b. Row looks (multi-line captions): re-measure each styled row with its
    //     font / weight / size; a row that no longer fits shrinks to fit.
    const rowSize = lines.map(() => 1);
    if (style.lineStyles && style.lineStyles.length) {
        lines.forEach((line, li) => {
            const rs = rowStyleAt(style, li);
            if (!rs)
                return;
            let size = rs.size && rs.size > 0 ? rs.size : 1;
            let row = line.map((m) => measureWord(m.ws, li, size));
            const w = row.reduce((a, m, i) => a + m.width + (i > 0 ? spaceWidth * size : 0), 0);
            if (w > maxWidth && w > 0) {
                size *= maxWidth / w;
                row = line.map((m) => measureWord(m.ws, li, size));
            }
            lines[li] = row;
            rowSize[li] = size;
        });
    }
    // 3. Position words. Compute box.
    const rowSpace = (li) => spaceWidth * rowSize[li];
    const lineWidths = lines.map((line, li) => line.reduce((acc, m, i) => acc + m.width + (i > 0 ? rowSpace(li) : 0), 0));
    const boxWidth = Math.max(0, ...lineWidths);
    // Per-line height: the isolated key-word line is scaled up (emphasis.scale),
    // so it needs a TALLER line height or it crowds/overlaps its neighbours.
    const emScale = Math.max(1, style.emphasis.scale || 1);
    const lineHeights = lines.map((line, li) => (newLineEmphasis && line.some((m) => m.ws.word.emphasized) ? lineHeightPx * emScale : lineHeightPx) * rowSize[li]);
    const lineTops = [];
    {
        let acc = 0;
        for (const lh of lineHeights) {
            lineTops.push(acc);
            acc += lh;
        }
    }
    const boxHeight = lineHeights.reduce((a, b) => a + b, 0);
    const centerX = placement.xNorm * frame.width;
    const centerY = anchorY(placement, frame, boxHeight);
    const boxTop = centerY - boxHeight / 2;
    const boxLeft = centerX - boxWidth / 2;
    const positioned = lines.map((line, li) => {
        const lw = lineWidths[li];
        let startX;
        if (style.textAlign === 'left')
            startX = boxLeft;
        else if (style.textAlign === 'right')
            startX = boxLeft + (boxWidth - lw);
        else
            startX = centerX - lw / 2;
        const baselineY = boxTop + lineTops[li] + lineHeights[li] * 0.72;
        let cursor = startX;
        return line.map((m) => {
            const cx = cursor + m.width / 2;
            const pw = {
                word: m.ws.word,
                index: m.ws.index,
                cx,
                baselineY,
                width: m.width,
                lineIndex: li,
                cssFont: m.cssFont,
                boldCssFont: m.cssFont,
                fontPx: m.fontPx,
                look: m.look,
            };
            cursor += m.width + rowSpace(li);
            return pw;
        });
    });
    return {
        fontSizePx,
        lineHeightPx,
        scale,
        cssFont: font,
        boldCssFont: boldFont,
        lines: positioned,
        box: { x: boxLeft, y: boxTop, width: boxWidth, height: boxHeight },
        frame,
    };
}
//# sourceMappingURL=layout.js.map